import type { ReactNode } from 'react';
import { usePage } from '@inertiajs/react';

import { ContentDate } from '@/components/page-sections/content-date';
import { usePageSectionData } from '@/components/page-sections/page-section-context';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ContentContainer } from '@/components/ui/content-container';
import { Separator } from '@/components/ui/separator';
import { Blockquote, Heading1, Paragraph } from '@/components/ui/typography';
import { resolvePublishAtInput } from '@/lib/page-scheduling';
import { cn, getInitials } from '@/lib/utils';
import type { Page, PageContentModel } from '@/types/page';
import type { SharedData } from '@/types/shared';
import {
  useActiveEditorPage,
  useResolvedContentModel,
} from '../metadata-controls/use-resolved-content-model';
import { formatProfileNames, normalizeProfiles } from '../profile-list';
import { type BlogProps, BlogVariant } from './definition';

interface MetadataItem {
  id: string;
  content: ReactNode;
}

export function resolveBlogRenderableDate({
  contentModel,
  isEditorPreview,
  page,
}: {
  contentModel: PageContentModel | null | undefined;
  isEditorPreview?: boolean;
  page: Page | null | undefined;
}) {
  if (contentModel?.publishedAt) return contentModel.publishedAt;
  // CMS preview uses the public page shell, so ignore editor-only picker state there.
  if (!isEditorPreview) return contentModel?.scheduledPublishAt ?? page?.scheduledPublishAt ?? null;

  const renderPage = page
    ? {
        ...page,
        scheduledPublishAt: contentModel?.scheduledPublishAt ?? page.scheduledPublishAt,
      }
    : null;
  return (
    resolvePublishAtInput(renderPage, isEditorPreview ? page?.contentModel : contentModel).value ||
    null
  );
}

export function BlogSection() {
  const { props, variant = BlogVariant.INLINE } = usePageSectionData<BlogProps>();
  const { isEditorPreview } = usePage<SharedData & { page?: Page }>().props;
  const title = props.title;
  const summary = props.summary;
  const body = props.body;
  const { contentModel } = useResolvedContentModel();
  const page = useActiveEditorPage();
  const publishedAt = resolveBlogRenderableDate({
    contentModel,
    isEditorPreview: Boolean(isEditorPreview),
    page,
  });
  const topics = contentModel?.topics ?? [];
  const authors = normalizeProfiles(contentModel?.authors);

  const isInline = variant === BlogVariant.INLINE;

  const metadataItems: MetadataItem[] = [];

  if (authors.length > 0) {
    const primaryAuthor = authors[0];

    metadataItems.push({
      id: 'authors',
      content: (
        <div className="flex items-center gap-3">
          <Avatar size="lg">
            <AvatarFallback className="text-sm font-semibold">
              {getInitials(primaryAuthor?.name)}
            </AvatarFallback>
          </Avatar>
          <div>
            <Paragraph className="text-sm text-muted-foreground">
              {authors.length > 1 ? 'Authors' : 'Author'}
            </Paragraph>
            <Paragraph className="font-medium">
              {formatProfileNames(authors) || 'Unknown Author'}
            </Paragraph>
          </div>
        </div>
      ),
    });
  }

  if (publishedAt) {
    metadataItems.push({
      id: 'date',
      content: <ContentDate value={publishedAt} />,
    });
  }

  if (topics.length > 0) {
    metadataItems.push({
      id: 'topics',
      content: (
        <div className="flex flex-wrap items-center gap-2">
          {topics.map((topic) => (
            <Badge key={topic.id} size="sm" colorScheme="primary" variant="solid">
              {topic.name}
            </Badge>
          ))}
        </div>
      ),
    });
  }

  return (
    <ContentContainer className="space-y-8">
      {title && <Heading1>{title}</Heading1>}

      {metadataItems.length > 0 && (
        <div
          className={cn(
            'flex gap-4 border-t border-b py-4',
            isInline ? 'flex-row flex-wrap items-center' : 'flex-col',
          )}
        >
          {metadataItems.map((item, index) => (
            <div key={item.id} className="flex items-center gap-2">
              {index > 0 && isInline && <Separator orientation="vertical" className="mx-2 h-4" />}
              {item.content}
            </div>
          ))}
        </div>
      )}

      {/* Blog Summary */}
      {summary && <Blockquote>{summary}</Blockquote>}

      {/* Blog Body Content */}
      {body && (
        <div className="prose max-w-none">
          {body.split('\n\n').map((paragraph, index) => (
            <Paragraph
              key={`paragraph-${index}-${paragraph.slice(0, 20)}`}
              className="mb-4 leading-relaxed"
            >
              {paragraph}
            </Paragraph>
          ))}
        </div>
      )}
    </ContentContainer>
  );
}
